/**
 * RoadmapGrid — the sortable table view, lifted out of RoadmapManager unchanged
 * so the kanban could become a peer view rather than a branch inside one file.
 *
 * Presentational: rows are pre-filtered and pre-sorted by the manager, and sort
 * clicks are reported upward.
 */
import {
  ownerLabel,
  priorityBadgeClass,
  statusBadgeClass,
  statusLabel,
} from './roadmapMeta';
import { formatRelative } from '../../utils/roadmapDates';
import type { RoadmapItem } from '../../types';
import type { RoadmapSortDir, RoadmapSortKey } from './roadmapSort';

interface RoadmapGridProps {
  rows: RoadmapItem[];
  sortKey: RoadmapSortKey;
  sortDir: RoadmapSortDir;
  onSort: (key: RoadmapSortKey) => void;
  onOpen: (item: RoadmapItem) => void;
}

export function RoadmapGrid({
  rows,
  sortKey,
  sortDir,
  onSort,
  onOpen,
}: RoadmapGridProps) {
  const headerClass = 'text-left font-medium px-3 py-2 select-none';

  const sortArrow = (key: RoadmapSortKey) => {
    if (key !== sortKey) return null;
    return (
      <span className="ml-1 text-gray-400">{sortDir === 'asc' ? '▲' : '▼'}</span>
    );
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-x-auto bg-white">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-500 border-b border-gray-200">
          <tr>
            <th
              className={`${headerClass} cursor-pointer`}
              onClick={() => onSort('title')}
            >
              Title{sortArrow('title')}
            </th>
            <th
              className={`${headerClass} cursor-pointer`}
              onClick={() => onSort('status')}
            >
              Status{sortArrow('status')}
            </th>
            <th
              className={`${headerClass} cursor-pointer`}
              onClick={() => onSort('priority')}
            >
              Priority{sortArrow('priority')}
            </th>
            <th className={headerClass}>Tags</th>
            <th
              className={`${headerClass} cursor-pointer`}
              onClick={() => onSort('owner')}
            >
              Owner{sortArrow('owner')}
            </th>
            <th
              className={`${headerClass} cursor-pointer`}
              onClick={() => onSort('estimatedScope')}
            >
              Scope{sortArrow('estimatedScope')}
            </th>
            <th
              className={`${headerClass} cursor-pointer whitespace-nowrap`}
              onClick={() => onSort('updatedAt')}
            >
              Updated{sortArrow('updatedAt')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((item) => (
            <tr
              key={item.id}
              onClick={() => onOpen(item)}
              className="hover:bg-gray-50 cursor-pointer"
            >
              <td className="px-3 py-2 font-medium text-gray-800 max-w-sm">
                <div className="truncate">
                  {(item.title ?? '').trim() || (
                    <span className="text-gray-300">(untitled)</span>
                  )}
                </div>
                {item.sourceMemoryFile && (
                  <span className="text-[10px] text-gray-400">
                    {item.sourceMemoryFile}
                  </span>
                )}
              </td>
              <td className="px-3 py-2">
                <span
                  className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(item.status)}`}
                >
                  {statusLabel(item.status)}
                </span>
              </td>
              <td className="px-3 py-2">
                <span
                  className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${priorityBadgeClass(item.priority)}`}
                >
                  {item.priority ?? '—'}
                </span>
              </td>
              <td className="px-3 py-2">
                {(item.tags ?? []).length === 0 ? (
                  <span className="text-gray-300">—</span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {(item.tags ?? []).map((tag) => (
                      <span
                        key={tag}
                        className="inline-block px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-600"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </td>
              <td className="px-3 py-2 text-gray-600">
                {ownerLabel(item.owner)}
              </td>
              <td className="px-3 py-2 text-gray-600">
                {item.estimatedScope ?? <span className="text-gray-300">—</span>}
              </td>
              <td className="px-3 py-2 text-gray-500 text-xs whitespace-nowrap">
                {formatRelative(item.updatedAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
